import apiService from '../../../services/apiService.js';

const JOBS_MONITOR_API = '/jobs-monitor';

const jobsMonitorService = {
  /**
   * Get job statistics (counts by status, active workers, etc.)
   */
  async getStats() {
    try {
      // Use longer timeout for stats query (can be slow with many jobs)
      const response = await apiService.get(`${JOBS_MONITOR_API}/stats`, {
        timeout: 60000  // 60 seconds for stats with large job counts
      });
      return response;
    } catch (error) {
      console.error('Error fetching job stats:', error);
      throw error;
    }
  },

  /**
   * Get recent jobs
   */
  async getRecentJobs(limit = 20) {
    try {
      const response = await apiService.get(`${JOBS_MONITOR_API}/recent?limit=${limit}`);
      return response.jobs || [];
    } catch (error) {
      console.error('Error fetching recent jobs:', error);
      throw error;
    }
  },

  /**
   * Get jobs by status
   */
  async getJobsByStatus(status, limit = 100) {
    try {
      const response = await apiService.get(`${JOBS_MONITOR_API}/status?status=${status}&limit=${limit}`);
      return response.items || [];
    } catch (error) {
      console.error('Error fetching jobs by status:', error);
      throw error;
    }
  },

  /**
   * Purge all jobs from the queue (DANGEROUS)
   */
  async purgeAllJobs() {
    try {
      // Use longer timeout for purge operation (60 seconds)
      const response = await apiService.delete(`${JOBS_MONITOR_API}/purge`, {
        timeout: 60000
      });
      return response;
    } catch (error) {
      console.error('Error purging jobs:', error);
      throw error;
    }
  },

  /**
   * Submit a PDF job for text extraction
   * For local dev: uses multipart/form-data
   * For deployed (Lambda): converts to base64 JSON
   */
  async submitPdfJob(file) {
    try {
      // Always use base64 for deployed Lambda (doesn't support multipart/form-data)
      // Lambda deployment: convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // Remove data:application/pdf;base64, prefix
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      const response = await apiService.post(`${JOBS_MONITOR_API}/submit/pdf`, {
        file: base64,
        fileName: file.name
      }, {
        timeout: 60000 // 60 second timeout for file upload
      });
      return response;
    } catch (error) {
      console.error('Error submitting PDF job:', error);
      throw error;
    }
  },

  /**
   * Submit a LinkedIn search job
   */
  async submitLinkedInJob(linkedin_username, linkedin_password, search_url, max_pages = 3, max_profiles = 100) {
    try {
      const response = await apiService.post(`${JOBS_MONITOR_API}/submit/linkedin`, {
        linkedin_username,
        linkedin_password,
        search_url,
        max_pages,
        max_profiles
      }, {
        timeout: 10000
      });
      return response;
    } catch (error) {
      console.error('Error submitting LinkedIn job:', error);
      throw error;
    }
  },

  /**
   * Submit a Terminix scrape job for an address
   */
  async submitTerminixScrapeJob(addressId, address, city, state, zipCode, buildingSqFt = null) {
    try {
      const url = `${JOBS_MONITOR_API}/submit/terminix-scrape`;
      console.log('[jobsMonitorService] Submitting terminix scrape job to:', url);
      console.log('[jobsMonitorService] Full URL will be:', window.location.origin + url);
      const response = await apiService.post(url, {
        addressId,
        address,
        city,
        state,
        zipCode,
        buildingSqFt
      }, {
        timeout: 10000
      });
      return response;
    } catch (error) {
      console.error('Error submitting Terminix scrape job:', error);
      throw error;
    }
  },

  /**
   * Get download URL for a completed job
   */
  async getJobDownloadUrl(jobId) {
    try {
      const response = await apiService.get(`${JOBS_MONITOR_API}/download?jobId=${jobId}`);
      return response;
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw error;
    }
  },

  /**
   * Submit multiple jobs for stress testing
   * Submits ALL jobs in PARALLEL for maximum stress testing realism
   * Backend reads real PDF files from TestingPDFs folder
   */
  async submitBulkJobs(pdfCount, linkedinCount) {
    try {
      // Make ONE API call to backend which creates all jobs at once
      // This simulates multiple users submitting simultaneously
      const response = await apiService.post(`${JOBS_MONITOR_API}/submit/bulk`, {
        pdfCount: parseInt(pdfCount) || 0,
        linkedinCount: parseInt(linkedinCount) || 0
      }, {
        timeout: 300000 // 5 minute timeout for bulk operations (large batches take time)
      });
      return response;
    } catch (error) {
      console.error('Error submitting bulk jobs:', error);
      throw error;
    }
  },

  /**
   * Get system pause status
   */
  async getSystemStatus() {
    try {
      const response = await apiService.get(`${JOBS_MONITOR_API}/system/status`);
      return response;
    } catch (error) {
      console.error('Error getting system status:', error);
      throw error;
    }
  },

  /**
   * Pause or resume job submissions
   */
  async setSystemPause(paused, reason = '') {
    try {
      const response = await apiService.post(`${JOBS_MONITOR_API}/system/pause`, {
        paused,
        reason
      });
      return response;
    } catch (error) {
      console.error('Error setting system pause:', error);
      throw error;
    }
  },

  async getResourceMetrics(forceRefresh = false) {
    try {
      const response = await apiService.get(`${JOBS_MONITOR_API}/resources${forceRefresh ? '?refresh=true' : ''}`);
      return response;
    } catch (error) {
      console.error('Error fetching resource metrics:', error);
      throw error;
    }
  },

  async getDeploymentInfo() {
    try {
      const response = await apiService.get(`${JOBS_MONITOR_API}/deployment-info`);
      return response;
    } catch (error) {
      console.error('Error fetching deployment info:', error);
      throw error;
    }
  }
};

export default jobsMonitorService;



